import React, { useEffect, useMemo, useState } from "react";
import { Plus, Edit2, Trash2, X, Search, RefreshCw, ChevronUp, ChevronDown, Upload } from "lucide-react";
import toast from "react-hot-toast";
import api, { getCategories, getHotels } from "../../services/api";

// ─── Types ────────────────────────────────────────────────────────────────────

type PricingMode = "fixed" | "cheapest_free" | "percent_off";
type OrderType = "pickup" | "delivery" | "kiosk";

interface MenuItemLite {
  _id: string;
  name: string;
  price: number;
  categoryId?: string | { _id: string };
}
interface CategoryLite {
  _id: string;
  name: string;
}
interface HotelLite {
  _id: string;
  hotelName: string;
}

interface SlotForm {
  name: string;
  quantity: number;
  categoryIds: string[];
  options: string[];
  upcharges: { itemId: string; amount: string }[];
}

interface DealForm {
  name: string;
  description: string;
  imageUrl: string;
  isActive: boolean;
  slots: SlotForm[];
  pricingMode: PricingMode;
  bundlePrice: string;
  discountPercent: string;
  showInCategoryIds: string[];
  showAtTop: boolean;
  startDate: string;
  endDate: string;
  availableDays: number[];
  startTime: string;
  endTime: string;
  orderTypes: OrderType[];
  hotelIds: string[];
  maxUsagePerOrder: string;
  bgColor: string;
  bgColorDark: string;
}

interface Deal {
  _id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  dealType: string;
  isActive: boolean;
  bundlePrice?: number;
  discountPercent?: number;
  pricingMode?: PricingMode;
  bundleSlots?: {
    name?: string;
    quantity?: number;
    options: (string | { _id: string })[];
    categoryIds?: string[];
    upcharges?: { itemId: string; amount: number }[];
  }[];
  showInCategoryIds?: string[];
  showAtTop?: boolean;
  sortOrder?: number;
  bgColor?: string;
  bgColorDark?: string;
  validFrom?: string | null;
  validTill?: string | null;
  availableDays?: number[];
  startTime?: string;
  endTime?: string;
  orderTypes?: OrderType[];
  hotelIds?: string[];
  maxUsagePerOrder?: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Deal dates are calendar days at the restaurants (Pacific time)
const TZ = "America/Vancouver";
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const ORDER_TYPES: { key: OrderType; label: string }[] = [
  { key: "pickup", label: "Pickup" },
  { key: "delivery", label: "Delivery" },
  { key: "kiosk", label: "Kiosk / In-store" },
];

const idOf = (v: any): string => String(v?._id ?? v ?? "");

/** UTC instant for a wall-clock time in the restaurants' time zone */
const zonedToUtc = (date: string, time: string): string => {
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm, ss] = time.split(":").map(Number);
  const guess = Date.UTC(y, m - 1, d, hh, mm, ss || 0);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date(guess));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asLocal = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour") % 24, get("minute"), get("second"));
  return new Date(guess - (asLocal - guess)).toISOString();
};

/** "YYYY-MM-DD" of an instant in the restaurants' time zone */
const utcToZonedDate = (iso?: string | null): string => {
  if (!iso) return "";
  return new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit" }).format(
    new Date(iso),
  );
};

const prettyDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { timeZone: TZ, month: "short", day: "numeric", year: "numeric" }) : "";

type Status = "active" | "scheduled" | "expired" | "hidden";
const isExpired = (deal: Deal) => !!deal.validTill && new Date(deal.validTill).getTime() < Date.now();
/** Inactive tab: switched to Unavailable, or its end date has passed */
const isInactive = (deal: Deal) => !deal.isActive || isExpired(deal);
const statusOf = (deal: Deal): Status => {
  if (!deal.isActive) return "hidden";
  const now = Date.now();
  if (deal.validTill && new Date(deal.validTill).getTime() < now) return "expired";
  if (deal.validFrom && new Date(deal.validFrom).getTime() > now) return "scheduled";
  return "active";
};
const STATUS_STYLE: Record<Status, { label: string; cls: string }> = {
  active: { label: "Active", cls: "bg-green-100 text-green-700" },
  scheduled: { label: "Scheduled", cls: "bg-blue-100 text-blue-700" },
  expired: { label: "Expired", cls: "bg-red-100 text-red-700" },
  hidden: { label: "Unavailable", cls: "bg-gray-200 text-gray-600" },
};

const emptySlot = (): SlotForm => ({ name: "", quantity: 1, categoryIds: [], options: [], upcharges: [] });
const emptyForm = (): DealForm => ({
  name: "",
  description: "",
  imageUrl: "",
  isActive: true,
  slots: [emptySlot()],
  pricingMode: "fixed",
  bundlePrice: "",
  discountPercent: "",
  showInCategoryIds: [],
  showAtTop: true,
  bgColor: "",
  bgColorDark: "",
  startDate: "",
  endDate: "",
  availableDays: [],
  startTime: "",
  endTime: "",
  orderTypes: [],
  hotelIds: [],
  maxUsagePerOrder: "0",
});

const priceSummary = (deal: Deal) => {
  const mode = deal.pricingMode || "fixed";
  if (mode === "cheapest_free") return "Cheapest item free";
  if (mode === "percent_off") return `${deal.discountPercent ?? 0}% off`;
  return deal.bundlePrice !== undefined ? `$${Number(deal.bundlePrice).toFixed(2)}` : "—";
};

// ─── Small UI pieces ──────────────────────────────────────────────────────────

const Chip: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active,
  onClick,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-full border px-3 py-1 text-xs transition ${
      active ? "border-blue-600 bg-blue-600 text-white" : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
    }`}
  >
    {children}
  </button>
);

const toggle = <T,>(list: T[], value: T) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

// ─── Page ─────────────────────────────────────────────────────────────────────

/**
 * Deals: slot-based deals ("Pick 2 Pizzas", "Any Burger + Any Drink + Fries")
 * priced as a fixed bundle, cheapest item free (BOGO) or % off. Each deal can
 * be shown in chosen menu categories (on top), limited by dates, days, hours,
 * order type and location, and hidden without deleting it.
 */
const DealsManager: React.FC = () => {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [items, setItems] = useState<MenuItemLite[]>([]);
  const [categories, setCategories] = useState<CategoryLite[]>([]);
  const [hotels, setHotels] = useState<HotelLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Active = available and not ended; Inactive = unavailable or end date has passed
  const [tab, setTab] = useState<"active" | "inactive">("active");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<DealForm>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [itemSearch, setItemSearch] = useState<Record<number, string>>({});

  const loadDeals = async () => {
    try {
      setLoading(true);
      const res = await api.get("/admin/getDeals", { params: { all: "true" } });
      setDeals(res.data?.data || []);
    } catch {
      toast.error("Failed to load deals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDeals();
    api
      .get("/menu/all")
      .then((res) => setItems(res.data?.data || []))
      .catch(() => setItems([]));
    getCategories()
      .then((res) => setCategories(res.data?.data || []))
      .catch(() => setCategories([]));
    getHotels()
      .then((res) => setHotels(res.data?.data || []))
      .catch(() => setHotels([]));
  }, []);

  const itemById = useMemo(() => new Map(items.map((i) => [i._id, i])), [items]);
  const categoryById = useMemo(() => new Map(categories.map((c) => [c._id, c.name])), [categories]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return deals.filter((d) => {
      if (q && !d.name.toLowerCase().includes(q)) return false;
      if ((tab === "inactive") !== isInactive(d)) return false;
      if (categoryFilter !== "all") {
        const ids = (d.showInCategoryIds || []).map(idOf);
        if (categoryFilter === "none" ? ids.length > 0 : !ids.includes(categoryFilter)) return false;
      }
      return true;
    });
  }, [deals, search, tab, categoryFilter]);
  const inactiveCount = useMemo(() => deals.filter(isInactive).length, [deals]);

  // ── List actions ──

  const toggleActive = async (deal: Deal) => {
    try {
      await api.put(`/admin/updateDeal/${deal._id}`, { isActive: !deal.isActive });
      setDeals((prev) => prev.map((d) => (d._id === deal._id ? { ...d, isActive: !deal.isActive } : d)));
      toast.success(deal.isActive ? "Deal unavailable" : "Deal available");
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to update deal");
    }
  };

  const move = async (index: number, dir: -1 | 1) => {
    const to = index + dir;
    if (to < 0 || to >= deals.length) return;
    const next = [...deals];
    const [moved] = next.splice(index, 1);
    next.splice(to, 0, moved);
    setDeals(next);
    try {
      await api.put("/admin/deals/reorder", { ids: next.map((d) => d._id) });
    } catch {
      toast.error("Failed to save the order");
      loadDeals();
    }
  };

  const remove = async (deal: Deal) => {
    if (!window.confirm(`Delete "${deal.name}"? Hide it instead if you may use it again.`)) return;
    try {
      await api.delete(`/admin/deleteDeal/${deal._id}`);
      setDeals((prev) => prev.filter((d) => d._id !== deal._id));
      toast.success("Deal deleted");
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to delete deal");
    }
  };

  // ── Form ──

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm());
    setItemSearch({});
    setShowForm(true);
  };

  const openEdit = (deal: Deal) => {
    setEditingId(deal._id);
    setItemSearch({});
    setForm({
      name: deal.name || "",
      description: deal.description || "",
      imageUrl: deal.imageUrl || "",
      isActive: deal.isActive !== false,
      slots: (deal.bundleSlots || []).length
        ? (deal.bundleSlots || []).map((s) => ({
            name: s.name || "",
            quantity: s.quantity || 1,
            categoryIds: (s.categoryIds || []).map(idOf),
            options: (s.options || []).map(idOf),
            upcharges: (s.upcharges || []).map((u) => ({ itemId: idOf(u.itemId), amount: String(u.amount) })),
          }))
        : [emptySlot()],
      pricingMode: deal.pricingMode || "fixed",
      bundlePrice: deal.bundlePrice !== undefined && deal.bundlePrice !== null ? String(deal.bundlePrice) : "",
      discountPercent: deal.discountPercent !== undefined && deal.discountPercent !== null ? String(deal.discountPercent) : "",
      showInCategoryIds: (deal.showInCategoryIds || []).map(idOf),
      showAtTop: deal.showAtTop !== false,
      bgColor: deal.bgColor || "",
      bgColorDark: deal.bgColorDark || "",
      startDate: utcToZonedDate(deal.validFrom),
      endDate: utcToZonedDate(deal.validTill),
      availableDays: deal.availableDays || [],
      startTime: deal.startTime || "",
      endTime: deal.endTime || "",
      orderTypes: deal.orderTypes || [],
      hotelIds: deal.hotelIds || [],
      maxUsagePerOrder: String(deal.maxUsagePerOrder ?? 0),
    });
    setShowForm(true);
  };

  const setSlot = (i: number, patch: Partial<SlotForm>) =>
    setForm((f) => ({ ...f, slots: f.slots.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));

  const uploadImage = async (file: File) => {
    const data = new FormData();
    data.append("image", file);
    try {
      setUploading(true);
      const res = await api.post<{ url: string }>("/upload-image", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, imageUrl: res.data.url }));
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const validate = (): string | null => {
    if (!form.name.trim()) return "Deal name is required";
    if (!form.slots.length) return "Add at least one item group";
    for (const [i, s] of form.slots.entries()) {
      if (!s.categoryIds.length && !s.options.length) return `Group ${i + 1}: choose a category or items`;
      if (!(s.quantity >= 1)) return `Group ${i + 1}: quantity must be at least 1`;
    }
    const picks = form.slots.reduce((n, s) => n + s.quantity, 0);
    if (form.pricingMode === "fixed" && !(Number(form.bundlePrice) > 0)) return "Enter the deal price";
    if (form.pricingMode === "percent_off") {
      const pct = Number(form.discountPercent);
      if (!(pct > 0 && pct <= 100)) return "Enter a discount between 1 and 100%";
    }
    if (form.pricingMode === "cheapest_free" && picks < 2) return "Buy-one-get-one needs at least 2 items picked";
    if (form.startDate && form.endDate && form.endDate < form.startDate) return "End date is before start date";
    if (!!form.startTime !== !!form.endTime) return "Set both start and end time, or neither";
    return null;
  };

  const save = async () => {
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }
    const body = {
      name: form.name.trim(),
      description: form.description.trim(),
      imageUrl: form.imageUrl,
      isActive: form.isActive,
      dealType: "bundle",
      // Keep an older deal's always-included items when editing it
      ...(editingId ? {} : { items: [] }),
      maxUsagePerOrder: Math.max(0, Math.floor(Number(form.maxUsagePerOrder) || 0)),
      bundleSlots: form.slots.map((s) => ({
        name: s.name.trim(),
        quantity: s.quantity,
        categoryIds: s.categoryIds,
        options: s.options,
        upcharges: s.upcharges
          .filter((u) => u.itemId && Number(u.amount) > 0)
          .map((u) => ({ itemId: u.itemId, amount: Number(u.amount) })),
        preselections: [],
      })),
      pricingMode: form.pricingMode,
      bundlePrice: form.pricingMode === "fixed" ? Number(form.bundlePrice) : undefined,
      discountPercent: form.pricingMode === "percent_off" ? Number(form.discountPercent) : undefined,
      showInCategoryIds: form.showInCategoryIds,
      showAtTop: form.showAtTop,
      bgColor: form.bgColor,
      bgColorDark: form.bgColorDark,
      validFrom: form.startDate ? zonedToUtc(form.startDate, "00:00:00") : "",
      validTill: form.endDate ? zonedToUtc(form.endDate, "23:59:59") : "",
      availableDays: form.availableDays,
      startTime: form.startTime,
      endTime: form.endTime,
      orderTypes: form.orderTypes,
      hotelIds: form.hotelIds,
    };
    try {
      setSaving(true);
      if (editingId) {
        await api.put(`/admin/updateDeal/${editingId}`, body);
        toast.success("Deal updated");
      } else {
        await api.post("/admin/createDeal", { ...body, sortOrder: deals.length });
        toast.success("Deal created");
      }
      setShowForm(false);
      loadDeals();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || "Failed to save deal");
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──

  return (
    <div className="mx-auto max-w-7xl p-4 pt-0 lg:p-8">
      <div className="sticky top-0 z-10 bg-white/90 py-6 backdrop-blur-sm">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="m-0 text-2xl font-bold text-gray-800">Deals</h1>
            <p className="m-0 mt-1 text-sm text-gray-500">
              Customers pick items for each group; deals show at the top of the categories you choose.
            </p>
          </div>
          <button
            onClick={openNew}
            className="inline-flex items-center gap-2 self-start rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            <Plus size={18} /> Add Deal
          </button>
        </div>
        <div className="mb-3 flex gap-1 border-b">
          {(
            [
              ["active", `Active (${deals.length - inactiveCount})`],
              ["inactive", `Inactive (${inactiveCount})`],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
                tab === key ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search deals..."
              className="w-full rounded-md border py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="h-10 rounded-md border px-2 text-sm"
          >
            <option value="all">All categories</option>
            <option value="none">Deals section only</option>
            {categories.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>
          <button
            onClick={loadDeals}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700"
            title="Refresh"
          >
            <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex min-h-[300px] items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      ) : shown.length === 0 ? (
        <div className="py-16 text-center text-gray-500">
          <p className="text-lg font-medium">No deals found</p>
          <p className="text-sm">Create one with "Add Deal".</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((deal) => {
            const status = statusOf(deal);
            const legacy = deal.dealType !== "bundle";
            const index = deals.findIndex((d) => d._id === deal._id);
            const cats = (deal.showInCategoryIds || []).map((c) => categoryById.get(idOf(c))).filter(Boolean);
            return (
              <div key={deal._id} className="flex flex-col overflow-hidden rounded-xl border bg-white shadow-sm">
                <div className="relative h-36 bg-gray-100">
                  {deal.imageUrl ? (
                    <img src={deal.imageUrl} alt={deal.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-3xl">🎁</div>
                  )}
                  <span className={`absolute left-2 top-2 rounded px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[status].cls}`}>
                    {STATUS_STYLE[status].label}
                  </span>
                  {legacy && (
                    <span className="absolute right-2 top-2 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                      Old type
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="m-0 text-base font-bold text-gray-800">{deal.name}</h3>
                    <span className="shrink-0 text-sm font-bold text-blue-600">{priceSummary(deal)}</span>
                  </div>
                  <p className="m-0 mt-2 text-xs text-gray-500">
                    Shows in: {cats.length ? cats.join(", ") : "Deals section"}
                    {cats.length ? (deal.showAtTop !== false ? " (top)" : " (after items)") : ""}
                  </p>
                  <p className="m-0 mt-1 text-xs text-gray-500">
                    {deal.validFrom ? `From ${prettyDate(deal.validFrom)} ` : ""}
                    {deal.validTill ? `Ends ${prettyDate(deal.validTill)}` : deal.validFrom ? "" : "No end date"}
                    {deal.availableDays?.length ? ` · ${deal.availableDays.map((d) => DAYS[d]).join(", ")}` : ""}
                    {deal.startTime && deal.endTime ? ` · ${deal.startTime}–${deal.endTime}` : ""}
                  </p>
                  <div className="mt-auto flex items-center justify-between gap-2 pt-4">
                    <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={deal.isActive} onChange={() => toggleActive(deal)} />
                      {deal.isActive ? "Available" : "Unavailable"}
                    </label>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => move(index, -1)}
                        disabled={index <= 0}
                        className="rounded p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                        title="Move up"
                      >
                        <ChevronUp size={16} />
                      </button>
                      <button
                        onClick={() => move(index, 1)}
                        disabled={index >= deals.length - 1}
                        className="rounded p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                        title="Move down"
                      >
                        <ChevronDown size={16} />
                      </button>
                      <button
                        onClick={() => openEdit(deal)}
                        disabled={legacy}
                        title={legacy ? "Old deal type — recreate it as a new deal to edit" : "Edit"}
                        className="rounded p-1.5 text-gray-600 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-30"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => remove(deal)}
                        className="rounded p-1.5 text-gray-600 hover:bg-red-50 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ===== Form ===== */}
      {showForm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-2 sm:p-4">
          <div className="flex max-h-[95vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h2 className="m-0 text-lg font-semibold">{editingId ? "Edit Deal" : "Add Deal"}</h2>
              <button onClick={() => setShowForm(false)} aria-label="Close">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-6 overflow-y-auto p-5">
              {/* Basics */}
              <section className="space-y-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium">Deal name *</label>
                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="e.g. Buy 1 Get 1 Free Pizza"
                      className="w-full rounded-md border p-2.5"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium">Image</label>
                    <div className="flex items-center gap-3">
                      {form.imageUrl && <img src={form.imageUrl} alt="" className="h-11 w-11 rounded object-cover" />}
                      <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-gray-50">
                        <Upload size={16} /> {uploading ? "Uploading..." : form.imageUrl ? "Change" : "Upload"}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])}
                        />
                      </label>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={2}
                    placeholder="e.g. Pick any 2 large pizzas — the cheaper one is free"
                    className="w-full rounded-md border p-2.5"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Card background</label>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {(
                      [
                        ["bgColor", "Light mode", "#ffffff"],
                        ["bgColorDark", "Dark mode", "#1f1f1f"],
                      ] as const
                    ).map(([key, label, fallback]) => (
                      <div key={key} className="flex items-center gap-2 rounded-md border p-2">
                        <input
                          type="color"
                          value={form[key] || fallback}
                          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                          className="h-9 w-12 cursor-pointer rounded border p-0.5"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="m-0 text-sm">{label}</p>
                          <p className="m-0 text-xs text-gray-500">{form[key] || "Default"}</p>
                        </div>
                        {form[key] && (
                          <button
                            type="button"
                            onClick={() => setForm({ ...form, [key]: "" })}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Reset
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="m-0 mt-1 text-xs text-gray-500">Text turns white or black on its own so it stays readable.</p>
                </div>
              </section>

              {/* Item groups */}
              <section>
                <h3 className="m-0 mb-1 text-sm font-semibold text-gray-800">What the customer picks</h3>
                <p className="m-0 mb-3 text-xs text-gray-500">
                  Each group is one step for the customer. Choose whole categories (new items join automatically)
                  and/or particular items.
                </p>
                <div className="space-y-4">
                  {form.slots.map((slot, i) => {
                    const q = (itemSearch[i] || "").toLowerCase();
                    const pickedItems = slot.options.map((id) => itemById.get(id)).filter(Boolean) as MenuItemLite[];
                    const upchargeChoices = Array.from(
                      new Map(
                        [
                          ...pickedItems,
                          ...items.filter((it) => slot.categoryIds.includes(idOf(it.categoryId))),
                        ].map((it) => [it._id, it]),
                      ).values(),
                    );
                    return (
                      <div key={i} className="rounded-lg border bg-gray-50 p-3">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-sm font-semibold">Group {i + 1}</span>
                          {form.slots.length > 1 && (
                            <button
                              type="button"
                              onClick={() => setForm((f) => ({ ...f, slots: f.slots.filter((_, j) => j !== i) }))}
                              className="text-xs text-red-600"
                            >
                              Remove group
                            </button>
                          )}
                        </div>
                        <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_120px]">
                          <input
                            value={slot.name}
                            onChange={(e) => setSlot(i, { name: e.target.value })}
                            placeholder='Step title, e.g. "Pick your Pizza"'
                            className="rounded-md border p-2 text-sm"
                          />
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600">Pick</span>
                            <input
                              type="number"
                              min={1}
                              value={slot.quantity}
                              onChange={(e) => setSlot(i, { quantity: Math.max(1, Number(e.target.value) || 1) })}
                              className="w-16 rounded-md border p-2 text-sm"
                            />
                          </div>
                        </div>
                        <p className="m-0 mb-1 text-xs font-medium text-gray-700">Any item from these categories</p>
                        <div className="mb-3 flex flex-wrap gap-1.5">
                          {categories.map((c) => (
                            <Chip
                              key={c._id}
                              active={slot.categoryIds.includes(c._id)}
                              onClick={() => setSlot(i, { categoryIds: toggle(slot.categoryIds, c._id) })}
                            >
                              {c.name}
                            </Chip>
                          ))}
                        </div>
                        <p className="m-0 mb-1 text-xs font-medium text-gray-700">…and/or these items</p>
                        {pickedItems.length > 0 && (
                          <div className="mb-2 flex flex-wrap gap-1.5">
                            {pickedItems.map((it) => (
                              <Chip key={it._id} active onClick={() => setSlot(i, { options: toggle(slot.options, it._id) })}>
                                {it.name} ×
                              </Chip>
                            ))}
                          </div>
                        )}
                        <input
                          value={itemSearch[i] || ""}
                          onChange={(e) => setItemSearch((s) => ({ ...s, [i]: e.target.value }))}
                          placeholder="Search items to add..."
                          className="mb-1 w-full rounded-md border p-2 text-sm"
                        />
                        {q && (
                          <div className="max-h-40 overflow-y-auto rounded-md border bg-white">
                            {items
                              .filter((it) => it.name.toLowerCase().includes(q) && !slot.options.includes(it._id))
                              .slice(0, 30)
                              .map((it) => (
                                <button
                                  key={it._id}
                                  type="button"
                                  onClick={() => {
                                    setSlot(i, { options: [...slot.options, it._id] });
                                    setItemSearch((s) => ({ ...s, [i]: "" }));
                                  }}
                                  className="flex w-full justify-between px-3 py-1.5 text-left text-sm hover:bg-blue-50"
                                >
                                  <span>{it.name}</span>
                                  <span className="text-gray-500">${Number(it.price).toFixed(2)}</span>
                                </button>
                              ))}
                          </div>
                        )}
                        {/* Upcharges */}
                        <details className="mt-2">
                          <summary className="cursor-pointer text-xs text-blue-700">
                            Extra charge for particular items ({slot.upcharges.length})
                          </summary>
                          <div className="mt-2 space-y-2">
                            {slot.upcharges.map((u, k) => (
                              <div key={k} className="flex items-center gap-2">
                                <select
                                  value={u.itemId}
                                  onChange={(e) =>
                                    setSlot(i, {
                                      upcharges: slot.upcharges.map((x, j) => (j === k ? { ...x, itemId: e.target.value } : x)),
                                    })
                                  }
                                  className="flex-1 rounded-md border p-1.5 text-sm"
                                >
                                  <option value="">Choose item</option>
                                  {upchargeChoices.map((it) => (
                                    <option key={it._id} value={it._id}>
                                      {it.name}
                                    </option>
                                  ))}
                                </select>
                                <span className="text-sm">+$</span>
                                <input
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  value={u.amount}
                                  onChange={(e) =>
                                    setSlot(i, {
                                      upcharges: slot.upcharges.map((x, j) => (j === k ? { ...x, amount: e.target.value } : x)),
                                    })
                                  }
                                  className="w-20 rounded-md border p-1.5 text-sm"
                                />
                                <button
                                  type="button"
                                  onClick={() => setSlot(i, { upcharges: slot.upcharges.filter((_, j) => j !== k) })}
                                  className="text-red-600"
                                  aria-label="Remove extra charge"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => setSlot(i, { upcharges: [...slot.upcharges, { itemId: "", amount: "" }] })}
                              className="text-xs text-blue-700"
                            >
                              + Add extra charge
                            </button>
                          </div>
                        </details>
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, slots: [...f.slots, emptySlot()] }))}
                    className="w-full rounded-md bg-gray-100 py-2 text-sm hover:bg-gray-200"
                  >
                    + Add group (e.g. a drink or fries)
                  </button>
                </div>
              </section>

              {/* Price */}
              <section>
                <h3 className="m-0 mb-2 text-sm font-semibold text-gray-800">Price</h3>
                <div className="mb-2 flex flex-wrap gap-2">
                  {(
                    [
                      ["fixed", "Fixed deal price"],
                      ["cheapest_free", "Buy one get one (cheapest free)"],
                      ["percent_off", "% off"],
                    ] as [PricingMode, string][]
                  ).map(([key, label]) => (
                    <Chip key={key} active={form.pricingMode === key} onClick={() => setForm({ ...form, pricingMode: key })}>
                      {label}
                    </Chip>
                  ))}
                </div>
                {form.pricingMode === "fixed" && (
                  <div className="flex items-center gap-2">
                    <span>$</span>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={form.bundlePrice}
                      onChange={(e) => setForm({ ...form, bundlePrice: e.target.value })}
                      placeholder="e.g. 20.00"
                      className="w-32 rounded-md border p-2"
                    />
                    <span className="text-xs text-gray-500">for all picked items (modifier extras charged on top)</span>
                  </div>
                )}
                {form.pricingMode === "percent_off" && (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={form.discountPercent}
                      onChange={(e) => setForm({ ...form, discountPercent: e.target.value })}
                      className="w-24 rounded-md border p-2"
                    />
                    <span>% off the picked items</span>
                  </div>
                )}
                {form.pricingMode === "cheapest_free" && (
                  <p className="m-0 text-xs text-gray-500">The lowest-priced picked item is free.</p>
                )}
              </section>

              {/* Where it shows */}
              <section>
                <h3 className="m-0 mb-1 text-sm font-semibold text-gray-800">Show in categories</h3>
                <p className="m-0 mb-2 text-xs text-gray-500">None chosen = a "Deals" section at the top of the menu.</p>
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {categories.map((c) => (
                    <Chip
                      key={c._id}
                      active={form.showInCategoryIds.includes(c._id)}
                      onClick={() => setForm({ ...form, showInCategoryIds: toggle(form.showInCategoryIds, c._id) })}
                    >
                      {c.name}
                    </Chip>
                  ))}
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.showAtTop}
                    onChange={(e) => setForm({ ...form, showAtTop: e.target.checked })}
                  />
                  Show at the top of the category
                </label>
              </section>

              {/* When / where */}
              <section className="space-y-3">
                <h3 className="m-0 text-sm font-semibold text-gray-800">Availability</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium">Start date (optional)</label>
                    <input
                      type="date"
                      value={form.startDate}
                      onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                      className="w-full rounded-md border p-2"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium">End date (optional — ends at 11:59 PM)</label>
                    <input
                      type="date"
                      value={form.endDate}
                      onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                      className="w-full rounded-md border p-2"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium">Days (none = every day)</label>
                  <div className="flex flex-wrap gap-1.5">
                    {DAYS.map((d, i) => (
                      <Chip
                        key={d}
                        active={form.availableDays.includes(i)}
                        onClick={() => setForm({ ...form, availableDays: toggle(form.availableDays, i).sort() })}
                      >
                        {d}
                      </Chip>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="text-xs font-medium">Hours (optional)</label>
                  <input
                    type="time"
                    value={form.startTime}
                    onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                    className="rounded-md border p-1.5"
                  />
                  <span>–</span>
                  <input
                    type="time"
                    value={form.endTime}
                    onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                    className="rounded-md border p-1.5"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium">Order types (none = all)</label>
                  <div className="flex flex-wrap gap-1.5">
                    {ORDER_TYPES.map((t) => (
                      <Chip
                        key={t.key}
                        active={form.orderTypes.includes(t.key)}
                        onClick={() => setForm({ ...form, orderTypes: toggle(form.orderTypes, t.key) })}
                      >
                        {t.label}
                      </Chip>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium">Locations (none = all)</label>
                  <div className="flex flex-wrap gap-1.5">
                    {hotels.map((h) => (
                      <Chip
                        key={h._id}
                        active={form.hotelIds.includes(h._id)}
                        onClick={() => setForm({ ...form, hotelIds: toggle(form.hotelIds, h._id) })}
                      >
                        {h.hotelName}
                      </Chip>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium">Max times per order</label>
                  <input
                    type="number"
                    min={0}
                    value={form.maxUsagePerOrder}
                    onChange={(e) => setForm({ ...form, maxUsagePerOrder: e.target.value })}
                    className="w-20 rounded-md border p-1.5 text-sm"
                  />
                  <span className="text-xs text-gray-500">0 = unlimited</span>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                  Show this deal to customers
                </label>
              </section>
            </div>

            <div className="flex justify-end gap-3 border-t px-5 py-4">
              <button onClick={() => setShowForm(false)} className="rounded-md bg-gray-200 px-4 py-2 hover:bg-gray-300">
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving || uploading}
                className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? "Saving..." : editingId ? "Save Deal" : "Create Deal"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealsManager;
